import { canonicalDigest } from '@djgg/gameplay-kit';
import { compositionLineageBuildModeV1, validateCompositionLineageV1, validateCompositionRevisionV1 } from '../compositionLineage.js';
import { assertCompositionConversionTargetV1 } from '../compositionConversion.js';
import { compositionMediaExecutionDigest, validateAcceptedCompositionV1, type CompositionGameSpecV1 } from '../spec/gameSpec.js';
import type { BuildJob, CreationVersion } from '../types.js';
import { compositionSuccessorMediaWindow, isCompositionMeasuredLimits } from './measuredBuildWindow.js';

/** The measured recipe revision selected by the accepted composition profile.
 * Courier remains on its closed v4 pipeline; maintained/composed profiles use
 * v5. Successor jobs must inherit this split just like their predecessors. */
export function compositionRecipeRevisionV1(profile: CompositionGameSpecV1['profile']): 4 | 5 {
  return profile === 'courier-short@1' ? 4 : 5;
}

/** Rechecked at execution, proof sealing and publication. Source archives and
 * generic recovery cannot supply executable inputs to a composed successor. */
export async function validateCompositionRoute(job: BuildJob, version: CreationVersion, expectedJobId: string) {
  const authority = await validateAcceptedCompositionV1(version.executionPolicy?.composition);
  const lineage = await validateCompositionLineageRoute(job, version, expectedJobId);
  const conversion = version.compositionConversion
    ? await assertCompositionConversionTargetV1(version.compositionConversion, authority)
    : undefined;
  const revision = version.executionPolicy?.compositionRevision
    ? await validateCompositionRevisionV1(version.executionPolicy.compositionRevision, authority)
    : undefined;
  const run = job.measuredExecution;
  // The ONE decision of whether this job funds and releases fresh media. A
  // successor's immutable authority still names its origin's media execution,
  // but the successor binds its lineage's refs and releases none.
  const mediaExecution = !lineage ? authority.media?.execution : undefined;
  const mediaWindow = lineage ? compositionSuccessorMediaWindow(authority) : mediaExecution;
  const mediaExecutionId = mediaExecution ? await compositionMediaExecutionDigest(mediaExecution) : undefined;
  if (job.version !== version.n || !run || run.recipe.revision !== compositionRecipeRevisionV1(authority.spec.profile) || run.compositionDigest !== authority.digest
    || run.compositionLineageDigest !== lineage?.digest
    || run.compositionRevisionDigest !== revision?.digest
    || run.compositionMediaExecutionDigest !== mediaExecutionId
    || version.pipelineSnapshot?.id !== 'measured-first-playable' || version.pipelineSnapshot.revision !== run.recipe.revision
    || version.pipelineSnapshot.config?.assembly !== (authority.spec.profile === 'courier-short@1' ? 'courier-short@1' : 'maintained-profiles@1')
    || version.executionPolicy?.providerPolicy !== 'zero-provider'
    || !isCompositionMeasuredLimits(run.limits, mediaWindow)
    || (!lineage && !conversion && !revision && !isCompositionOriginHead(version))
    || (!!conversion && (version.n <= conversion.source.version
      || conversion.source.creationId !== job.creationId
      || version.executionPolicy?.executionKind !== 'composition-conversion'
      || !!authority.media))
    || (version.executionPolicy?.executionKind === 'composition-revision') !== !!revision
    || (!!revision && (!!lineage || !!conversion || version.n <= revision.predecessor.version
      || revision.predecessor.creationId !== job.creationId))
    || version.gates.acceptedRecovery
    || authority.intent.inputDigest !== await canonicalDigest(version.prompt)
    || authority.intent.settingsDigest !== await canonicalDigest(version.settingsSnapshot ?? null)
    || authority.intent.designDigest !== await canonicalDigest(version.designSnapshot ?? null)) {
    throw new Error('Composition route authority changed');
  }
  // Publishing/live versions have their own output source reference. Before
  // publishing no archive, including an accepted predecessor archive, is seed.
  if (version.srcPath && !['publishing', 'live'].includes(version.buildStatus)) throw new Error('Composed route cannot import archived source');
  return { authority, lineage, conversion, revision, mediaExecution, mediaWindow };
}

/** A composed origin is the first version, or the code-create of a composed
 * game whose first build failed (accepted only as such). Execution and
 * publication both admit exactly this head. */
export function isCompositionOriginHead(version: CreationVersion): boolean {
  return version.n === 1 || version.executionPolicy?.executionKind === 'code-create';
}

/** Whether a composed parent's ACCEPTED execution still carries its released
 * media. Media follows that execution, not the parent's momentary claim: a
 * structural phase that spends the request segment commits the barrier, wakes
 * the media and then yields, so the parent sits `queued` with its checkpoint
 * retained for a successor claim while its media runs (1983-mario-composed-6:
 * the yield came 0.5 s after the barrier, and requiring `claimed` killed the
 * media 0.7 s later, then the successor). Open means: a committed barrier, and
 * either a segment holds the claim or the execution is between segments with
 * a continuation for its retained checkpoint. A claim whose lease expired
 * (a lost worker, until the reaper turns it into a continuation or a failure),
 * a terminal parent, or a queued one with no retained continuation is closed.
 * Callers check the media deadline, revocations and the authority digests
 * beside this. */
export function compositionMediaParentOpen(parent: BuildJob, now: number): boolean {
  const run = parent.measuredExecution;
  if (!run?.compositionBarrier) return false;
  if (parent.status === 'claimed') return !!parent.claimToken && (parent.leaseExpiry ?? 0) > now;
  return parent.status === 'queued' && !!run.checkpoint
    && run.continuation?.checkpointRevision === run.checkpoint.revision;
}

export async function validateCompositionLineageRoute(job: BuildJob, version: CreationVersion, expectedJobId: string) {
  const rawLineage = version.executionPolicy?.compositionLineage;
  const lineage = rawLineage ? await validateCompositionLineageV1(rawLineage,
    await validateAcceptedCompositionV1(version.executionPolicy?.composition)) : undefined;
  if (job.measuredExecution?.compositionLineageDigest !== lineage?.digest
    || version.creationRun?.codeJobId !== expectedJobId) throw new Error('Composition lineage binding changed');
  const creationRun = version.creationRun!;
  if (!lineage) {
    if (version.buildMode) throw new Error('Unsupported composition origin route');
  } else {
    // A fork is a different creation's v1; a rebind and a presentation
    // republish replace the same creation's predecessor, which only a rebind
    // also names as its art-completion seed.
    if (version.buildMode !== compositionLineageBuildModeV1(lineage.kind)
      || creationRun.visualJobId || creationRun.musicJobId
      || (lineage.kind === 'fork-copy' ? version.n !== 1 || job.creationId === lineage.predecessor.creationId
        : job.creationId !== lineage.predecessor.creationId || version.n <= lineage.predecessor.version
          || (lineage.kind === 'asset-rebind' ? version.artCompletionOf !== lineage.predecessor.version
            : !!version.artCompletionOf))) throw new Error('Composition successor route changed');
  }
  return lineage;
}
